import { useState, useCallback } from 'react'
import { useRouter } from 'next/router'
import { cn } from '~/src/lib/utils'
import { PanelLeftIcon, PanelLeftCloseIcon, SearchIcon } from 'lucide-react'
import DupeIconLogo from '~/src/components/svg/DupeIconLogo'
import SaleTagsIcon from '~/src/components/svg/SaleTagsIcon'
import AppIcon from '~/src/components/svg/AppIcon'
import TradeIcon from '~/src/components/svg/TradeIcon'
import BlogIcon from '~/src/components/svg/BlogIcon'
import ProductsIcon from '~/src/components/svg/ProductsIcon'
import RewardsIcon from '~/src/components/svg/RewardsIcon'
import MenuRecents from './home/MenuRecents'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAuthUser from '~/src/hooks/useAuthUser'
import ProviderButton from './ProviderButton'
import { GoogleAuthProvider } from '~/src/lib/firebase/firebaseAuth'
import Link from 'next/link'
import { PowerIcon } from 'lucide-react'
import { useIntercom } from 'react-use-intercom'
import {
  ExclusiveTooltipProvider,
  ExclusiveTooltipTrigger,
  useHideAllTooltips,
} from './ui/exclusive-tooltip'
import NavRecentsIcon from '~/src/components/svg/NavRecentsIcon'
import { useHideDesktopSideNav } from '~/src/hooks/useHideDesktopSideNav'
import DupeLogo from '~/src/components/svg/DupeLogo'
import useFetchRecents from '~/src/hooks/useFetchRecents'
import { Badge } from '~/src/components/ui/badge'
import useGetUserActivityCounts from '~/src/hooks/user/useGetUserActivityCounts'

// Global utility function for clearing all tooltips
export const clearAllTooltipsGlobal = () => {
  try {
    // 1. Hide tooltips
    const tooltipElements = document.querySelectorAll(
      '[data-radix-tooltip-content], [role="tooltip"], [data-state*="open"]'
    )
    tooltipElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.style.display = 'none'
        element.style.visibility = 'hidden'
        element.style.opacity = '0'
      }
    })

    // Trigger mouse leave events on tooltip triggers to init a close
    const tooltipTriggers = document.querySelectorAll(
      '.dupe-tooltip-trigger, [data-radix-tooltip-trigger], [data-radix-tooltip-content], [role="tooltip"]'
    )
    tooltipTriggers.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }))
        element.dispatchEvent(new MouseEvent('blur', { bubbles: true }))
      }
    })
  } catch (error) {
    // noop
  }
}

const DesktopSideNav: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const router = useRouter()
  const { signOut, authUserNotAnonymous } = useAuthUser()
  const { userData, openAuthModal } = useAuthContext()
  const { showNewMessage } = useIntercom()
  const hideAllTooltips = useHideAllTooltips()
  const hideDesktopSideNav = useHideDesktopSideNav()
  const isHome = router.pathname === '/'

  // Check if user has recents to display
  const { data: recentsData } = useFetchRecents({
    limit: 1, // Only need to check if there are any recents
    includeTotal: false,
  })
  const hasRecents = (recentsData?.recents?.length ?? 0) > 0

  // Comprehensive tooltip clearing function
  const clearAllTooltips = useCallback(() => {
    // 1. Clear exclusive tooltips
    hideAllTooltips()

    // 2. Use global utility for other tooltip systems
    clearAllTooltipsGlobal()
  }, [hideAllTooltips])

  const handleExpand = () => {
    clearAllTooltips()
    setIsExpanded(true)
  }

  const handleCollapse = () => {
    clearAllTooltips()
    setIsExpanded(false)
  }

  if (hideDesktopSideNav) return null

  return (
    <ExclusiveTooltipProvider>
      <div
        className={cn(
          'fixed left-0 top-0 z-50 h-[100vh] shadow-lg transition-all duration-300 ease-in-out dark:border-white/10',
          {
            'bg-black text-white dark:bg-black dark:text-white': isHome,
            'border-r border-black/10 bg-white text-black dark:bg-black dark:text-white':
              !isHome,
          }
        )}
        style={{
          width: isExpanded ? 320 : 'var(--sidebar-width-collapsed)',
        }}
        onClick={(e) => {
          // Hide tooltips when clicking on any interactive element
          clearAllTooltips()

          // Don't expand if clicking on navigation links or buttons
          const target = e.target as HTMLElement
          const isNavigationLink = target.closest('a[href]')
          const isButton = target.closest('button')

          if (isNavigationLink || isButton) {
            return
          }

          // if currentTarget has the cursor e-resize, then expand
          const isResizing = e.currentTarget.style.cursor === 'e-resize'
          if (isResizing) {
            handleExpand()
            return
          }

          // Only expand if clicking on the background (not on interactive elements)
          if (e.target === e.currentTarget) {
            handleExpand()
          }
        }}
      >
        <div className='group flex h-full flex-col'>
          {/* Header */}
          <div
            className={cn(
              'flex items-center justify-between pb-4 pt-6',
              isExpanded ? 'px-4' : 'mx-auto'
            )}
          >
            {isExpanded ? (
              <div className='flex w-full items-center justify-between'>
                <Link href='/'>
                  <DupeLogo className='h-7' />
                </Link>
                <ExclusiveTooltipTrigger
                  id='collapse-sidebar'
                  content={<p>Collapse sidebar</p>}
                  isHome={isHome}
                >
                  <button
                    onClick={handleCollapse}
                    className={cn(
                      'relative flex h-8 w-8 cursor-w-resize items-center justify-center rounded-lg transition-colors before:absolute before:inset-[-2px] before:-z-10 before:rounded-lg before:transition-colors before:content-[""]'
                    )}
                  >
                    <PanelLeftCloseIcon className={cn('h-6 w-6')} />
                  </button>
                </ExclusiveTooltipTrigger>
              </div>
            ) : (
              <ExclusiveTooltipTrigger
                id='expand-sidebar'
                content={<p>Click to expand sidebar</p>}
                isHome={isHome}
              >
                <button
                  onClick={handleExpand}
                  className={cn(
                    'relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors before:absolute before:inset-[-2px] before:-z-10 before:rounded-lg before:transition-colors before:content-[""]'
                  )}
                >
                  <DupeIconLogo className='h-9 transition-opacity group-hover:opacity-0' />
                  <PanelLeftIcon
                    className={cn(
                      'absolute h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100'
                    )}
                  />
                </button>
              </ExclusiveTooltipTrigger>
            )}
          </div>

          {/* Navigation Content */}
          <div className='relative flex-1 overflow-y-auto'>
            <div className='p-4'>
              <UnifiedNavItems
                setIsExpanded={setIsExpanded}
                hideAllTooltips={clearAllTooltips}
                isHome={isHome}
                hasRecents={hasRecents}
                isExpanded={isExpanded}
              />
            </div>
          </div>

          {/* Footer */}
          <div
            className={cn(
              'border-t border-black/10 p-4 dark:border-white/10',
              isExpanded ? 'px-4' : 'border-none px-2'
            )}
          >
            {authUserNotAnonymous ? (
              isExpanded ? (
                <div className='flex items-center justify-between'>
                  <Link
                    href='/profile'
                    className='truncate text-sm font-medium opacity-50 hover:underline'
                  >
                    {userData?.email || userData?.phone || 'Profile'}
                  </Link>
                  <div className='flex items-center gap-2'>
                    <button
                      onClick={() => showNewMessage()}
                      className='text-sm font-medium opacity-50 hover:underline'
                    >
                      Help
                    </button>
                    <button onClick={() => signOut()} className='opacity-50'>
                      <PowerIcon className='h-4 w-4' strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ) : null
            ) : isExpanded ? (
              <div className='flex flex-col gap-4'>
                <p className='text-sm opacity-50'>
                  Access the best deals, earn cash for using Dupe, save all your
                  searches!
                </p>
                <ProviderButton
                  size='md'
                  providerId={GoogleAuthProvider.PROVIDER_ID}
                  onClick={() => {
                    openAuthModal({
                      source: 'side-nav-signup',
                    })
                  }}
                >
                  Sign up
                </ProviderButton>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </ExclusiveTooltipProvider>
  )
}

// Unified navigation items for both collapsed and expanded states
const UnifiedNavItems: React.FC<{
  setIsExpanded: (expanded: boolean) => void
  hideAllTooltips: () => void
  isHome: boolean
  hasRecents?: boolean
  isExpanded: boolean
}> = ({ setIsExpanded, hideAllTooltips, isHome, hasRecents, isExpanded }) => {
  const { data: activityCounts } = useGetUserActivityCounts({
    enabled: isExpanded,
  })

  const totalUnclaimedPoints = activityCounts?.totalUnclaimedPoints ?? 0
  const totalClaimedPoints = activityCounts?.totalClaimedPoints ?? 0
  const totalPoints = totalClaimedPoints || totalUnclaimedPoints

  type NavItem = {
    href: string
    label: string
    icon: React.ReactElement
    onClick?: () => void
    badge?: React.ReactNode
    target?: string
  }

  // Define groups with their items
  const navGroups: NavItem[][] = [
    // Group 1: New search, products, sales
    [
      {
        href: '/',
        label: 'New Search',
        icon: <SearchIcon className={cn('h-6 w-6')} />,
      },
      {
        href: '/products/furniture',
        label: 'Products',
        icon: <ProductsIcon className={cn('h-6 w-6')} />,
      },
      {
        href: '/clearance',
        label: 'Sales',
        icon: <SaleTagsIcon className={cn('h-6 w-6')} />,
      },
    ],
    // Group 2: App, trade, blog
    [
      {
        href: '/app',
        label: 'App',
        icon: <AppIcon className={cn('w-6')} />,
      },
      {
        href: '/trade',
        label: 'Trade',
        icon: <TradeIcon className={cn('h-6 w-6')} />,
        badge: isExpanded ? (
          <Badge className='!bg-orange-400 text-xs text-white'>Pro</Badge>
        ) : undefined,
      },
      {
        href: '/blog',
        label: 'Blog',
        icon: <BlogIcon className={cn('h-6 w-6')} />,
      },
    ],
    // Group 3: Rewards
    [
      {
        href: '/$dupe/rewards',
        label: 'Rewards',
        icon: <RewardsIcon className={cn('h-6 w-6')} />,
        badge: isExpanded ? (
          <span className='bg-dupelight rounded-md px-1.5 py-0.5 text-xs font-semibold text-black'>
            {totalPoints} pts
          </span>
        ) : undefined,
      },
    ],
  ]

  // Only add recents group if user has recents and sidebar is collapsed
  if (hasRecents && !isExpanded) {
    navGroups.push([
      {
        href: '#',
        label: 'Recents',
        icon: <NavRecentsIcon className={cn('h-6 w-6')} />,
        onClick: () => {
          hideAllTooltips()
          setIsExpanded(true)
        },
      },
    ])
  }

  // if isHome, remove the "New Search" item
  if (isHome && navGroups[0]) {
    navGroups[0] = navGroups[0].filter((item) => item.label !== 'New Search')
  }

  return (
    <div className='flex flex-col gap-2'>
      {navGroups.map((group, groupIndex) => (
        <div
          key={`unified-group-${groupIndex}`}
          className='flex flex-col items-center gap-2'
        >
          {group.map((item, index) => {
            const navItem = (
              <Link
                href={item.href}
                target={item.target}
                className={cn(
                  'relative flex h-8 items-center rounded-lg transition-colors',
                  'hover:bg-black/5 dark:hover:bg-white/10',
                  {
                    'w-full gap-3': isExpanded,
                    'w-8 justify-center': !isExpanded,
                  }
                )}
                onClick={(e) => {
                  if (item.onClick) {
                    e.preventDefault()
                    item.onClick()
                  }
                }}
              >
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center opacity-30 transition-opacity hover:opacity-50',
                    {
                      relative: isExpanded,
                    }
                  )}
                >
                  <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
                    {item.icon}
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <span
                    className={cn(
                      'line-clamp-1 text-base font-medium transition-all duration-300',
                      {
                        'w-auto opacity-80': isExpanded,
                        'w-0 overflow-hidden opacity-0': !isExpanded,
                      }
                    )}
                  >
                    {item.label}
                  </span>
                  {item.badge && (
                    <span
                      className={cn('transition-all duration-300', {
                        'opacity-100': isExpanded,
                        'opacity-0': !isExpanded,
                      })}
                    >
                      {item.badge}
                    </span>
                  )}
                </div>
              </Link>
            )

            return isExpanded ? (
              <div
                key={`unified-nav-${groupIndex}-${index}`}
                className='w-full'
              >
                {navItem}
              </div>
            ) : (
              <ExclusiveTooltipTrigger
                key={`unified-nav-${groupIndex}-${index}`}
                id={`unified-nav-${groupIndex}-${index}`}
                content={<p>{item.label}</p>}
                isHome={isHome}
              >
                {navItem}
              </ExclusiveTooltipTrigger>
            )
          })}
          {/* Add spacing between groups (except for the last group) */}
          {groupIndex < navGroups.length - 1 && <div className='h-[20px]' />}
        </div>
      ))}

      {/* Recents section - only show when expanded */}
      {isExpanded && hasRecents && (
        <div className='-mx-3 mt-4'>
          <MenuRecents />
        </div>
      )}
    </div>
  )
}

export default DesktopSideNav
